import React from 'react'
import { useProjects } from '../hooks/useProject'
import { LoadingSkeleton } from '../components/common/LoadingSkeleton'

interface KPICardProps {
  title: string
  value: string
  subtitle: string
  trend: 'up' | 'down' | 'neutral'
  color: 'blue' | 'green' | 'yellow' | 'red'
}

const KPICard: React.FC<KPICardProps> = ({ title, value, subtitle, trend, color }) => {
  const colorClasses = {
    blue: 'border-blue-200 bg-blue-50',
    green: 'border-green-200 bg-green-50',
    yellow: 'border-yellow-200 bg-yellow-50',
    red: 'border-red-200 bg-red-50'
  }

  const trendIcon = {
    up: '↑',
    down: '↓',
    neutral: '→'
  }

  return (
    <div className={`rounded-lg border p-6 shadow-sm ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
        <div className="text-2xl">{trendIcon[trend]}</div>
      </div>
    </div>
  )
}

const MainDashboardPage: React.FC = () => {
  const { data: projects, isLoading } = useProjects()

  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (!projects) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-500">No projects data available</p>
        </div>
      </div>
    )
  }

  // Calculate overall KPIs
  const totalProjects = projects.length
  const activeProjects = projects.filter(project => project.status === 'active').length
  const completedProjects = projects.filter(project => project.status === 'completed').length
  const draftProjects = projects.filter(project => project.status === 'draft').length

  const activePercentage = totalProjects > 0 ? Math.round((activeProjects / totalProjects) * 100) : 0
  const completedPercentage = totalProjects > 0 ? Math.round((completedProjects / totalProjects) * 100) : 0
  const draftPercentage = totalProjects > 0 ? Math.round((draftProjects / totalProjects) * 100) : 0

  // Calculate project value statistics
  const projectsWithValue = projects.filter(project => project.totalValue)
  const totalProjectValue = projectsWithValue.reduce((sum, project) => sum + (project.totalValue || 0), 0)
  const averageProjectValue = projectsWithValue.length > 0 ? totalProjectValue / projectsWithValue.length : 0

  // Calculate project duration statistics
  const projectsWithDates = projects.filter(project => project.startDate && project.endDate)
  const projectDurations = projectsWithDates.map(project => {
    const startDate = new Date(project.startDate || '')
    const endDate = new Date(project.endDate || '')
    return (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
  })
  const averageDuration = projectDurations.length > 0 ? Math.round(projectDurations.reduce((a, b) => a + b, 0) / projectDurations.length) : 0

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Application Dashboard</h1>
            <p className="text-gray-600 mt-1">Comprehensive overview and analytics of all projects</p>
          </div>
        </div>
      </div>

      {/* Enhanced KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KPICard
          title="Total Projects"
          value={totalProjects.toString()}
          subtitle={`${activeProjects} active, ${completedProjects} completed`}
          trend="neutral"
          color="blue"
        />
        <KPICard
          title="Project Success Rate"
          value={`${completedPercentage}%`}
          subtitle={`${completedProjects} of ${totalProjects} completed`}
          trend={completedPercentage > 50 ? 'up' : 'down'}
          color="green"
        />
        <KPICard
          title="Active Projects"
          value={`${activePercentage}%`}
          subtitle={`${activeProjects} currently active`}
          trend={activePercentage > 50 ? 'up' : 'down'}
          color="yellow"
        />
        <KPICard
          title="Draft Projects"
          value={`${draftPercentage}%`}
          subtitle={`${draftProjects} in planning phase`}
          trend={draftPercentage < 20 ? 'up' : 'down'}
          color="red"
        />
      </div>

      {/* Financial Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial Overview</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Total Project Value</p>
                <p className="text-2xl font-bold text-blue-800">Rp {totalProjectValue.toLocaleString()}</p>
              </div>
              <div className="text-blue-600">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v.01" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18c-1.657 0-3-.895-3-2s1.343-2 3-2 3-.895 3-2-1.343-2-3-2m0 8c1.11 0 2.08-.402 2.599-1M12 18V19m0-1v.01" />
                </svg>
              </div>
            </div>
            <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Average Project Value</p>
                <p className="text-2xl font-bold text-green-800">Rp {averageProjectValue.toLocaleString()}</p>
              </div>
              <div className="text-green-600">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Timeline</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Average Duration</p>
                <p className="text-2xl font-bold text-purple-800">{averageDuration} days</p>
              </div>
              <div className="text-purple-600">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="flex justify-between items-center p-4 bg-indigo-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Projects with Timeline</p>
                <p className="text-2xl font-bold text-indigo-800">{projectsWithDates.length} of {totalProjects}</p>
              </div>
              <div className="text-indigo-600">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Project Status Distribution */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Project Status Distribution</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-green-100 rounded-lg p-6 text-center">
            <div className="text-green-600 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-green-800 font-medium">ACTIVE PROJECTS</p>
            <p className="text-3xl font-bold text-green-900 mt-2">{activeProjects}</p>
            <p className="text-sm text-green-700 mt-1">{activePercentage}% of total</p>
          </div>
          <div className="bg-blue-100 rounded-lg p-6 text-center">
            <div className="text-blue-600 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-blue-800 font-medium">COMPLETED PROJECTS</p>
            <p className="text-3xl font-bold text-blue-900 mt-2">{completedProjects}</p>
            <p className="text-sm text-blue-700 mt-1">{completedPercentage}% of total</p>
          </div>
          <div className="bg-gray-100 rounded-lg p-6 text-center">
            <div className="text-gray-600 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.368a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
            </div>
            <p className="text-sm text-gray-800 font-medium">DRAFT PROJECTS</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{draftProjects}</p>
            <p className="text-sm text-gray-700 mt-1">{draftPercentage}% of total</p>
          </div>
        </div>
      </div>

      {/* Projects Overview */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Projects Overview</h2>
        <div className="space-y-4">
          {projects.map((project) => (
            <div key={project.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex-1">
                <p className="font-medium text-gray-900">{project.name}</p>
                <p className="text-sm text-gray-500">{project.location || 'No location specified'}</p>
                {project.totalValue && (
                  <p className="text-sm text-gray-600 mt-1">Value: Rp {project.totalValue.toLocaleString()}</p>
                )}
              </div>
              <div className="flex items-center gap-4">
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                  project.status === 'active' ? 'bg-green-100 text-green-800' :
                  project.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {project.status.replace('_', ' ').toUpperCase()}
                </span>
                <span className="text-sm text-gray-500">ID: {project.id}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Value Distribution</h2>
          <div className="space-y-3">
            {projectsWithValue.length > 0 ? (
              projectsWithValue.map((project) => (
                <div key={project.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{project.name}</p>
                    <p className="text-xs text-gray-500">{project.location || 'No location'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-blue-600">Rp {project.totalValue?.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">
                      {Math.round(((project.totalValue || 0) / totalProjectValue) * 100)}% of total
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No project value data available</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Statistics</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-green-600 mb-2">
                <svg className="w-8 h-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-green-800">{activeProjects}</p>
              <p className="text-xs text-green-600 mt-1">Active</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-blue-600 mb-2">
                <svg className="w-8 h-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-blue-800">{completedProjects}</p>
              <p className="text-xs text-blue-600 mt-1">Completed</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-yellow-600 mb-2">
                <svg className="w-8 h-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-yellow-800">{projectsWithDates.length}</p>
              <p className="text-xs text-yellow-600 mt-1">With Timeline</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-purple-600 mb-2">
                <svg className="w-8 h-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v.01" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-purple-800">{projectsWithValue.length}</p>
              <p className="text-xs text-purple-600 mt-1">With Value</p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Application Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">Total Projects</p>
            <p className="text-3xl font-bold text-gray-900">{totalProjects}</p>
            <p className="text-xs text-gray-500 mt-2">All projects in the system</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">Total Value</p>
            <p className="text-3xl font-bold text-blue-800">Rp {totalProjectValue.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-2">Combined value of all projects</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">Success Rate</p>
            <p className="text-3xl font-bold text-green-800">{completedPercentage}%</p>
            <p className="text-xs text-gray-500 mt-2">Completed projects percentage</p>
          </div>
        </div>
      </div>

      {/* Project Health Indicators */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Health Indicators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-green-600 mb-2">
              <svg className="w-10 h-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-green-800">Healthy</p>
            <p className="text-2xl font-bold text-green-900">{activeProjects}</p>
            <p className="text-xs text-green-700 mt-1">Active Projects</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-blue-600 mb-2">
              <svg className="w-10 h-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-blue-800">Completed</p>
            <p className="text-2xl font-bold text-blue-900">{completedProjects}</p>
            <p className="text-xs text-blue-700 mt-1">Successful Projects</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 text-center">
            <div className="text-yellow-600 mb-2">
              <svg className="w-10 h-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-yellow-800">In Progress</p>
            <p className="text-2xl font-bold text-yellow-900">{projectsWithDates.length}</p>
            <p className="text-xs text-yellow-700 mt-1">With Timeline</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <div className="text-purple-600 mb-2">
              <svg className="w-10 h-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v.01" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-purple-800">Planned</p>
            <p className="text-2xl font-bold text-purple-900">{projectsWithValue.length}</p>
            <p className="text-xs text-purple-700 mt-1">With Budget</p>
          </div>
        </div>
      </div>

      {/* Detailed Project Metrics */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Detailed Project Metrics</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projects.map((project) => {
                const duration = project.startDate && project.endDate ? (
                  (new Date(project.endDate).getTime() - new Date(project.startDate).getTime()) / (1000 * 60 * 60 * 24)
                ) : 0
                return (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{project.name}</div>
                          <div className="text-sm text-gray-500">ID: {project.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        project.status === 'active' ? 'bg-green-100 text-green-800' :
                        project.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {project.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {project.totalValue ? `Rp ${project.totalValue.toLocaleString()}` : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{project.location || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{duration > 0 ? `${Math.round(duration)} days` : 'N/A'}</div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default MainDashboardPage